// frontend/src/pages/SalesPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { productService, Product, CreateProductData } from '../services/productService';
import saleService, { Sale, SaleType, PaymentMethod } from '../services/saleService';
import ticketService, { CreateTicketRequest } from '../services/ticketService';
import customerService from '../services/customerService';
import { useAuth } from '../context/AuthContext';
import userService from '../services/userService';
import { User as AppUser, UserRole } from '../types';
import { formatCurrency } from '../utils/format';
import toast from 'react-hot-toast';
import {
  ShoppingCart,
  Trash2,
  Plus,
  Search,
  QrCode,
  User,
  Package,
  AlertCircle,
  Scissors,
  FileText,
  Lock,
  Unlock,
  Printer,
  Eye,
  X,
  CornerDownRight,
} from 'lucide-react';
import { SaleDetailModal } from '../components/SaleDetailModal';

import { useCart, CartItem } from '../context/CartContext';
import { ScannerModal } from '../components/ScannerModal';
import { useGlobalScanner } from '../hooks/useGlobalScanner';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { TicketOptionsModal } from '../components/TicketOptionsModal';
import { printTicketWindow, sendTicketWhatsApp } from '../utils/ticketUtils';

import CustomCombobox from '../components/common/Combobox';

import { CashCutModal } from '../components/sales/CashCutModal';
import { PreOrderFlowModal, PreOrderFlow } from '../components/sales/PreOrderFlowModal';

export const SalesPage: React.FC = () => {
  // Estados de productos y búsqueda
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showTransferConfirmation, setShowTransferConfirmation] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showCashCutModal, setShowCashCutModal] = useState(false); // Cash Cut Modal State
  const [isMobileCartOpen, setIsMobileCartOpen] = useState(false); // Mobile Cart State


  // Estados para sugerencias en "Pedir Producto"
  const [existingBrands, setExistingBrands] = useState<string[]>([]);
  const [existingCodes, setExistingCodes] = useState<string[]>([]);
  const [brandColors, setBrandColors] = useState<{ brand: string; color: string }[]>([]);
  const [brandSizes, setBrandSizes] = useState<{ brand: string; size: string }[]>([]);
  const [codeBrands, setCodeBrands] = useState<{ code: string; brand: string }[]>([]);



  const { 
    cart, addToCart, removeFromCart, updateQuantity, clearCart, totalAmount, 
    customer, setCustomer,
    isWholesaleMode, setIsWholesaleMode
  } = useCart();
  const [saleType, setSaleType] = useState<SaleType>('SALE');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH');


  // Admin / Vendedora Logic
  const { user } = useAuth();
  const isAdmin = user?.rol_usr === UserRole.ADMIN;
  const isOnlineSeller = user?.rol_usr === UserRole.VENDEDORA_EN_LINEA;
  const [adminSelectedSeller, setAdminSelectedSeller] = useState<AppUser | null>(null);
  const [sellersList, setSellersList] = useState<AppUser[]>([]);

  useEffect(() => {
    if (isAdmin || isOnlineSeller) {
      setIsSalesMode(false); // Force disable Sales Mode for admins and online sellers
    }

    if (isAdmin) {
      // Load sellers
      const fetchSellers = async () => {
        try {
          const [vendedoras, vendedorasOnline] = await Promise.all([
            userService.getAllUsers({ role: UserRole.VENDEDORA, status: 'true' }),
            userService.getAllUsers({ role: UserRole.VENDEDORA_EN_LINEA, status: 'true' })
          ]);
          setSellersList([...vendedoras, ...vendedorasOnline]);
        } catch (e) {
          console.error(e);
          toast.error('Error al cargar lista de vendedoras');
        }
      };
      // Avoiding duplicate fetches if user/admin status doesn't change
      fetchSellers();
    }
  }, [user?.rol_usr]);

  // Derived state for inputs (sync with global store)
  // We can use the store directly in inputs' value prop, and update store on change
  // Simplifying: no local state needed if we sync directly.

  // Search Customer State
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [showCustomerOptions, setShowCustomerOptions] = useState(false);
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const productSearchRef = useRef<HTMLDivElement>(null);

  // Close search dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowCustomerOptions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchContainerRef]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(async () => {
      if (customerSearchTerm.length > 1) {
        setIsSearchingCustomer(true);
        try {
          const response = await customerService.getCustomers({ search: customerSearchTerm, limit: 10, simple: true });
          setCustomerOptions(response.data);
          setShowCustomerOptions(true);
        } catch (e) {
          console.error(e);
        } finally {
          setIsSearchingCustomer(false);
        }
      } else {
        setCustomerOptions([]);
        setShowCustomerOptions(false);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [customerSearchTerm]);

  const selectCustomer = (selected: any) => {
    setCustomer({
      name: selected.fullName,
      phone: selected.phone || '',
      email: selected.email || ''
    });
    setCustomerSearchTerm('');
    setShowCustomerOptions(false);
    toast.success('Cliente seleccionado');
  };

  // Estado de Descuento (Inputs)
  const [discountPercentage, setDiscountPercentage] = useState<string>('');
  const [discountReason, setDiscountReason] = useState('');
  const [payments, setPayments] = useState<{ method: PaymentMethod; amount: number }[]>([{ method: 'CASH', amount: 0 }]); // Initialized in effects
  const [isLayaway, setIsLayaway] = useState(false);
  const [layawayAmount, setLayawayAmount] = useState(''); // Kept for legacy ref, but mostly derived or synced

  // Estado de Totales (Derivados)
  const grossTotal = totalAmount; // Suma de productos
  const discountVal = discountPercentage ? Math.round(grossTotal * (parseFloat(discountPercentage) / 100)) : 0;

  // Costos adicionales (Solo para VENDEDORA_EN_LINEA o si se requiere)
  const [shippingCost, setShippingCost] = useState(0);
  const [alterationCost, setAlterationCost] = useState(0);

  // Set default shipping for Online Sellers
  useEffect(() => {
    if (isOnlineSeller) {
      setShippingCost(500);
    } else {
      setShippingCost(0);
    }
  }, [isOnlineSeller]);

  const total = (grossTotal - discountVal) + shippingCost + alterationCost;



  // Estados de ajustes/ticket
  const [needsAdjustments, setNeedsAdjustments] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [pendingSale, setPendingSale] = useState<any>(null);

  // Historial de ventas
  const [allSales, setAllSales] = useState<Sale[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // const [totalGeneral, setTotalGeneral] = useState(0);
  // const [totalMonth, setTotalMonth] = useState(0);
  const itemsPerPage = 50;

  const [filterCustomer, setFilterCustomer] = useState('');
  const [debouncedFilterCustomer, setDebouncedFilterCustomer] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [debouncedGlobalSearch, setDebouncedGlobalSearch] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<PaymentMethod | 'ALL'>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [filterSeller, setFilterSeller] = useState('');
  const [debouncedFilterSeller, setDebouncedFilterSeller] = useState('');

  // Estados para filtros de historial (Opciones)
  const [allCustomerNames, setAllCustomerNames] = useState<string[]>([]);
  const [allSellerNames, setAllSellerNames] = useState<string[]>([]);

  // Cargar datos para filtros cuando se abre el historial
  useEffect(() => {
    if (showHistory) {
      const loadFilterOptions = async () => {
        try {
          // Cargar Vendedoras
          const [vendedoras, vendedorasOnline, admins] = await Promise.all([
            userService.getAllUsers({ role: UserRole.VENDEDORA, status: 'true' }),
            userService.getAllUsers({ role: UserRole.VENDEDORA_EN_LINEA, status: 'true' }),
            userService.getAllUsers({ role: UserRole.ADMIN, status: 'true' })
          ]);

          const sellers = [...vendedoras, ...vendedorasOnline, ...admins];
          const uniqueSellerNames = Array.from(new Set(sellers.map(u => u.nombre_completo || u.username).filter(Boolean)));
          setAllSellerNames(uniqueSellerNames);

          // Cargar Clientes (Limitado a 1000 recientes para rendimiento, o búsqueda dinámica idealmente)
          // Para simplificar UX, cargamos una buena cantidad de nombres únicos
          const customersResponse = await customerService.getCustomers({ limit: 1000, simple: true });
          const uniqueCustomerNames = Array.from(new Set(customersResponse.data.map(c => c.fullName).filter(Boolean)));
          setAllCustomerNames(uniqueCustomerNames);

        } catch (error) {
          console.error('Error loading filter options', error);
          toast.error('Error al cargar opciones de filtro');
        }
      };
      loadFilterOptions();
    }
  }, [showHistory]);

  // Estado de carga
  const [loading, setLoading] = useState(false);
  const [showSaleDetailModal, setShowSaleDetailModal] = useState(false);
  const [selectedSaleForDetail, setSelectedSaleForDetail] = useState<Sale | null>(null);

  // Modo Ventas (Global)
  const [isSalesMode, setIsSalesMode] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingDeductInventory, setPendingDeductInventory] = useState(false);
  const [tempSellerAuth, setTempSellerAuth] = useState<{ code: string, pass: string } | null>(null);
  const [eventDate, setEventDate] = useState<string>(''); // Nuevo estado para fecha de evento

  // Estado para el modal de opciones de ticket
  const [showTicketOptionsModal, setShowTicketOptionsModal] = useState(false);
  const [selectedSaleForTicket, setSelectedSaleForTicket] = useState<Sale | null>(null);

  // Estado para el modal de flujo Pre-order
  const [showPreOrderFlowModal, setShowPreOrderFlowModal] = useState(false);
  const [preOrderFlow, setPreOrderFlow] = useState<PreOrderFlow>(null);
  const [pendingPreOrderProduct, setPendingPreOrderProduct] = useState<string>('');

  // Estado para ConfirmationModal
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: React.ReactNode;
    onConfirm: () => void;
    confirmText?: string;
    color?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });

  // Estado para Crear Producto (Nuevo Requerimiento)
  const [showProductModal, setShowProductModal] = useState(false);
  const [newProductData, setNewProductData] = useState<CreateProductData>({
    cod_producto: '',
    color: '',
    marca: '',
    precio: 0,
    talla: '',

    stock: 0,
    activo: false, // Por defecto inactivo hasta que llegue
    codigo_proveedor: '',
    type: 'maestro'
  });

  // Estados para variantes múltiples (Color × Talla)
  const [createMultiple, setCreateMultiple] = useState(false);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);

  const [customColor, setCustomColor] = useState('');
  const [customSize, setCustomSize] = useState('');

  // Load options when opening the modal
  useEffect(() => {
    if (showProductModal) {
      const loadOptions = async () => {
        try {
          const options = await productService.getFilterOptions();
          const m = options.maestro || {} as any;
          const n = options.novias || {} as any;

          const allMarcas = Array.from(new Set([...(m.marcas || []), ...(n.marcas || []), ...(options.marcas || [])]));
          const allCodes = Array.from(new Set([...(m.codes || []), ...(n.codes || [])]));
          const allBrandColors = [...(m.brandColors || []), ...(n.brandColors || [])];
          const allBrandSizes = [...(m.brandSizes || []), ...(n.brandSizes || [])]; // Fetch sizes
          const allCodeBrands = [...(m.codeBrands || []), ...(n.codeBrands || [])];

          setExistingBrands(allMarcas);
          setExistingCodes(allCodes);
          setBrandColors(allBrandColors);
          setBrandSizes(allBrandSizes); // Set sizes
          setCodeBrands(allCodeBrands);
        } catch (e) {
          console.error("Error loading options", e);
        }
      };
      loadOptions();
    }
  }, [showProductModal]);

  // --- SCANNER LOGIC START ---
  // --- PRODUCT FIND AND ADD (SHARED LOGIC) ---
  const findAndAddProduct = async (code: string) => {
    if (!code.trim() || loading) return;

    setLoading(true);
    // toast('Buscando: ' + code); // Optional: verbose feedback

    try {
      // 1. Search product by code (general search covers cod_producto, etc.)
      const response = await productService.getProducts({
        search: code,
        limit: 5,
        type: 'all',
        exactMatch: true // FAST LANE enabled
      });

      const foundProducts = response.data;

      // 2. Exact match filter
      const exactMatch = foundProducts.find(p =>
        (p.qr_code_value && p.qr_code_value === code) ||
        (p.cod_producto && p.cod_producto === code) ||
        (p.codigo_proveedor && p.codigo_proveedor === code)
      );

      const productToAdd = exactMatch || foundProducts[0];

      if (productToAdd) {
        handleAddToCart(productToAdd);
        // Clear search if successful to allow continuous scanning
        setSearchTerm('');

        // Ensure focus remains on input (optional, depending on browser behavior, generally automatic)
      } else {
        toast.error('Producto no encontrado: ' + code);
        // Do NOT clear search term on error, so user can correct it
      }
    } catch (error) {
      console.error(error);
      toast.error('Error buscando producto: ' + code);
    } finally {
      setLoading(false);
    }
  };

  // --- SCANNER LOGIC START ---
  const handleScan = async (decodedText: string) => {
    // Wrapper for camera scanner
    await findAndAddProduct(decodedText);
    setShowScanner(false);
  };
  // --- SCANNER LOGIC END ---

  // Handle Enter key in search input
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission if any
      findAndAddProduct(searchTerm);
    }
  };

  // Derived amount paid
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);

  // Sync layawayAmount string for compatibility
  useEffect(() => {
    // Check numeric difference to avoid overwriting "50." with "50" while typing
    const currentVal = parseFloat(layawayAmount) || 0;
    if (Math.abs(currentVal - totalPaid) > 0.001) {
      setLayawayAmount(totalPaid.toString());
    }
  }, [totalPaid]);

  useEffect(() => {
    // Detectar items PRE-ORDER
    const hasPreOrderItems = cart.some(item => (item.product.stock || 0) < item.quantity || item.product.pre_order);

    // Si se eliminan todos los items Pre-order, resetear el flujo
    if (!hasPreOrderItems && preOrderFlow) {
      setPreOrderFlow(null);
      setIsLayaway(false);
    }

    // Auto-update payments
    if (!isLayaway) {
      // En venta normal, el pago único siempre iguala al total actual
      setPayments([{ method: paymentMethod, amount: total }]);
    } else if (hasPreOrderItems && payments.length === 1) {
      // If PRE-ORDER active and layaway chosen, set 50% if amount is 0 OR if it equals TOTAL
      const currentAmount = payments[0].amount;
      if (currentAmount === 0 || Math.abs(currentAmount - total) < 0.1) {
        setPayments([{ method: paymentMethod, amount: total * 0.5 }]);
      }
    }
  }, [cart, discountPercentage, isLayaway, paymentMethod, total]); // Dependencies

  // Global Scanner Listener
  useGlobalScanner({
    onScan: handleScan,
    enabled: true
  });

  // ... (Restore existing Debounce effects) ...
  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterSeller(filterSeller);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterSeller]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterCustomer(filterCustomer);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterCustomer]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGlobalSearch(globalSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [globalSearch]);

  useEffect(() => {
    loadProducts(debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1); // Reset page on filter change
  }, [debouncedFilterSeller, debouncedFilterCustomer, debouncedGlobalSearch, filterStatus, filterPaymentMethod, filterDateFrom, filterDateTo]);

  useEffect(() => {
    loadSales();
  }, [debouncedFilterSeller, debouncedFilterCustomer, debouncedGlobalSearch, filterStatus, filterPaymentMethod, filterDateFrom, filterDateTo, currentPage, showHistory]);

  // ... (Restore loadProducts, loadSales, filteredSales...) ...

  // Only replacement for the area where handleScan was, but I need to ensure I don't lose the code below it if I replace a large chunk.
  // The original code had:
  // 153: handleScan = ...
  // 210: useGlobalScanner...
  // 218: useEffect debounce...
  // 240: useEffect loadSales...

  // I will replace valid chunk from existing handleScan start (line 153) to end of effects (line 243) or similar.
  // Actually, I will just inject the `findAndAddProduct` and `handleSearchKeyDown` before useGlobalScanner, and update `handleScan`.
  // Wait, I can't replace scattered lines.
  // Strategy: Replace from `const handleScan` (153) to `const loadProducts` (244) roughly.
  // Let me verify lines again.
  // Line 153: const handleScan...
  // Line 244: const loadProducts... 
  // Okay, replacing that block.
  const fetchCount = useRef(0);

  const loadProducts = async (search = '') => {
    const currentFetchId = ++fetchCount.current;
    try {
      setLoading(true);
      // Cargar productos con búsqueda en backend
      const response = await productService.getProducts({
        limit: 1000,
        search,
        type: 'all'
      });

      if (currentFetchId !== fetchCount.current) return;

      setProducts(response.data.filter((p) => p.activo !== false));
    } catch (error) {
      if (currentFetchId !== fetchCount.current) return;
      toast.error('Error al cargar productos');
    } finally {
      if (currentFetchId === fetchCount.current) {
        setLoading(false);
      }
    }
  };

  const loadSales = async () => {
    if (!showHistory) return; // Only load if history is open to save initial requests

    try {
      setLoading(true);
      const params: any = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      };

      if (debouncedFilterSeller) params.sellerName = debouncedFilterSeller;
      if (debouncedFilterCustomer) params.customerName = debouncedFilterCustomer;
      if (debouncedGlobalSearch) params.q = debouncedGlobalSearch;
      if (filterStatus !== 'ALL') params.status = filterStatus;
      if (filterPaymentMethod !== 'ALL') params.paymentMethod = filterPaymentMethod;
      if (filterDateFrom) params.startDate = filterDateFrom;
      if (filterDateTo) params.endDate = filterDateTo;

      const response = await saleService.getSales(params);
      setAllSales(response.data);
      if (response.total !== undefined) setTotalCount(response.total);
      // if (response.totalGeneral !== undefined) setTotalGeneral(response.totalGeneral);
      // if (response.totalMonth !== undefined) setTotalMonth(response.totalMonth);

    } catch (error) {
      console.error('Error al cargar ventas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Productos ya filtrados por backend
  const filteredProducts = products;

  // Agregar producto a la nota de venta
  const handleAddToCart = (product: Product) => {
    const stock = product.stock || 0;
    const cartItem = cart.find((item) => item.product.id_producto === product.id_producto);
    const currentQty = cartItem ? cartItem.quantity : 0;

    // Validación de stock
    if (stock > 0 && currentQty >= stock) {
      if (stock === 1) {
        toast.error('Este vestido ya está en la nota de venta y solo hay 1 unidad disponible en stock.');
      } else {
        toast.error(`Solo hay ${stock} unidades disponibles en stock.`);
      }
      return;
    }

    if (stock <= 0) {
      toast('Producto sin stock - Se agregará para PRE-ORDER', {
        icon: <AlertCircle className="w-5 h-5 text-yellow-500" />
      });
    }

    addToCart(product);
    toast.success(`${product.marca} ${product.color} agregado`);
  };

  // Actualizar cantidad de producto en nota de venta
  const handleUpdateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveFromCart(productId);
      return;
    }

    const cartItem = cart.find((item) => item.product.id_producto === productId);
    if (cartItem) {
      const stock = cartItem.product.stock || 0;
      // Si estamos aumentando la cantidad (quantity > cart.quantity)
      if (quantity > cartItem.quantity && stock > 0 && quantity > stock) {
        if (stock === 1) {
          toast.error('Solo hay 1 unidad disponible en stock.');
        } else {
          toast.error(`Solo hay ${stock} unidades disponibles en stock.`);
        }
        return;
      }
    }

    updateQuantity(productId, quantity);
  };

  // Remover producto de la nota de venta
  const handleRemoveFromCart = (productId: number) => {
    removeFromCart(productId);
  };

  // Limpiar nota de venta
  const handleClearCart = () => {
    clearCart();
    setCustomer({ name: '', phone: '', email: '' });
    setEventDate('');
    setPayments([{ method: 'CASH', amount: 0 }]);
    setPaymentMethod('CASH');
    setIsLayaway(false);
    setLayawayAmount('');
    setDiscountPercentage('');
    setDiscountReason('');
    setShippingCost(0);
    setAlterationCost(0);
    setNeedsAdjustments(false);
    setPreOrderFlow(null);
    setCustomerSearchTerm('');
  };



  // Procesar venta
  const handleProcessSale = async () => {
    if (customer.name.trim() === '') {
      toast.error('Nombre del cliente es requerido');
      return;
    }

    if (cart.length === 0) {
      toast.error('La nota de venta está vacía');
      return;
    }

    // Detectar si hay items Pre-order
    const hasPreOrderItems = cart.some(item => (item.product.stock || 0) < item.quantity || item.product.pre_order);

    // Si hay items Pre-order y no se ha seleccionado flujo, intentar determinarlo automáticamente
    if (hasPreOrderItems && !preOrderFlow) {
      const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
      
      if (isLayaway) {
        // El usuario ya marcó manualmente que es un Apartado
        setPreOrderFlow('layaway');
      } else if (Math.abs(totalPaid - total) < 0.01) {
        // Es un pago completo
        setPreOrderFlow('fullPayment');
      } else {
        // Si no está claro, mostrar el modal como respaldo
        const firstPreOrderItem = cart.find(item => (item.product.stock || 0) < item.quantity || item.product.pre_order);
        const productName = firstPreOrderItem ? `${firstPreOrderItem.product.marca} ${firstPreOrderItem.product.color}` : '';
        setPendingPreOrderProduct(productName);
        setShowPreOrderFlowModal(true);
        return;
      }
    }

    // Validar que el flujo seleccionado tenga los pagos correctos
    if (hasPreOrderItems && preOrderFlow) {
      const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);

      if (preOrderFlow === 'layaway') {
        // No se requiere mínimo forzoso, pero se recomienda 50%
        // Asegurar que isLayaway está activado
        if (!isLayaway) {
          setIsLayaway(true);
        }
      } else if (preOrderFlow === 'fullPayment') {
        if (Math.abs(totalPaid - total) > 0.01) {
          toast.error('Para pago completo, debe liquidarse el 100% del total');
          return;
        }
        // Asegurar que isLayaway está desactivado
        if (isLayaway) {
          setIsLayaway(false);
        }
      }
    }

    // Verificación de stock eliminada para permitir flujo automático (mixed logic handle in completeSale)
    // const hasInsufficientStock = cart.some(item => (item.product.stock || 0) < item.quantity);

    // Advertencia para Transferencia (Solo Vendedora en línea o Admin seleccionado como tal)
    const isActingAsOnlineSeller = isOnlineSeller || (isAdmin && adminSelectedSeller?.rol_usr === UserRole.VENDEDORA_EN_LINEA);

    if (isActingAsOnlineSeller && paymentMethod === 'TRANSFER') {
      setShowTransferConfirmation(true);
      return;
    }

    executeSaleProcess();
  };

  const executeSaleProcess = async () => {
    // Si es VENTA
    if (saleType === 'SALE') {
      // Validar que los artículos existentes tengan stock suficiente
      const outOfStockItem = cart.find(item => !(item.product as any).isTemp && (item.product.stock || 0) < item.quantity);
      if (outOfStockItem) {
        toast.error(`El artículo "${outOfStockItem.product.marca} ${outOfStockItem.product.color || ''}" no tiene existencia suficiente en inventario.`);
        return;
      }
    }

    // Validación para Apartado (Specific Logic)
    if (saleType === 'SALE' && isLayaway) {
      const amount = Number(layawayAmount);
      if (!amount || amount <= 0) {
        toast.error('Ingresa un monto válido para el apartado');
        return;
      }
      if (amount >= total) {
        toast.error('El monto de apartado debe ser menor al total. Si es pago completo, desmarca "Apartado".');
        return;
      }

      // Detectar si hay items que requieren PRE-ORDER (sin stock o stock insuficiente)
      // Ya no se valida el 50% como requisito forzoso

      // Si hay items PRE-ORDER, se recomienda 50% pero no es obligatorio
      // (eliminado requisito forzoso)

      // Apartado implica descontar inventario (se aparta el producto)
      setPendingDeductInventory(true);

      if (isAdmin) {
        if (!adminSelectedSeller) {
          toast.error('Selecciona una vendedora primero');
          return;
        }
        completeSale(true); // Deduct for Layaway
        return;
      }

      if (isSalesMode && !isOnlineSeller) {
        setShowAuthModal(true);
      } else {
        const hasPreOrderItems = cart.some(item => {
          const stock = item.product.stock || 0;
          return stock < item.quantity;
        });

        setConfirmModal({
          isOpen: true,
          title: hasPreOrderItems ? 'Confirmar PRE-ORDER' : 'Confirmar Apartado',
          message: (
            <div>
              {hasPreOrderItems && (
                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 text-yellow-600 shrink-0" />
                  <span><strong>PRE-ORDER:</strong> Esta venta incluye artículos que requieren pedido</span>
                </div>
              )}
              <p className="mb-2">¿Estás seguro de registrar {hasPreOrderItems ? 'este PRE-ORDER' : 'este apartado'}?</p>
              <div className={`${hasPreOrderItems ? 'bg-yellow-50 border-yellow-100' : 'bg-purple-50 border-purple-100'} p-3 rounded-lg border text-sm`}>
                <p className="flex justify-between mb-1">
                  <span className="text-gray-600">Total Venta:</span>
                  <span className="font-bold">{formatCurrency(total)}</span>
                </p>
                <p className={`flex justify-between mb-1 font-bold ${hasPreOrderItems ? 'text-yellow-700' : 'text-purple-700'}`}>
                  <span>Anticipo:</span>
                  <span>{formatCurrency(amount)}</span>
                </p>
                <p className="flex justify-between border-t border-purple-200 pt-1 mt-1 text-gray-500">
                  <span>Restante:</span>
                  <span>{formatCurrency(total - amount)}</span>
                </p>
              </div>
            </div>
          ),
          confirmText: 'Confirmar Apartado',
          color: 'purple',
          onConfirm: () => completeSale(true)
        });
      }
      return;
    }

    // Si es RENTA, procesar directamente
    // Si está en Modo Ventas, pedir autenticación

    // Determine default deduct status
    // IF SALE -> Default to TRUE (Auto-deduct)
    // IF RENT -> Default to FALSE (or whatever previous logic was)
    const shouldDeduct = saleType === 'SALE';

    if (isAdmin) {
      if (!adminSelectedSeller) {
        toast.error('Como administrador, debes seleccionar una vendedora');
        return;
      }
      await completeSale(shouldDeduct);
    } else if (isSalesMode && !isOnlineSeller) {
      setPendingDeductInventory(shouldDeduct);
      setShowAuthModal(true);
    } else {
      await completeSale(shouldDeduct);
    }
  };

  // Completar venta con o sin descuento de inventario
  const completeSale = async (deductInventory: boolean, authData?: { code: string; pass: string }) => {
    setLoading(true);
    setShowInventoryModal(false);
    setShowAuthModal(false);

    try {
      // 🔍 Detectar productos temporales
      const tempProducts = cart.filter(item => (item.product as any).isTemp);
      const existingProducts = cart.filter(item => !(item.product as any).isTemp);

      // ✅ SI HAY PRODUCTOS TEMPORALES, usar endpoint especial
      if (tempProducts.length > 0) {
        console.log(`🔸 Detectados ${tempProducts.length} productos temporales, creando atómicamente...`);

        const saleWithNewProductsData = {
          // Productos nuevos a crear — expandir según cantidad para generar registros individuales
          newProducts: tempProducts.flatMap(item => {
            const qty = Math.max(1, item.quantity);
            const base = {
              cod_producto: item.product.cod_producto,
              color: item.product.color,
              marca: item.product.marca,
              precio: item.product.precio,
              costo: item.product.costo,
              talla: item.product.talla,
              stock: 0,
              activo: false,
              pre_order: true,
              codigo_proveedor: (item.product as any).codigo_proveedor || '',
              type: (item.product as any).type || 'maestro',
              quantity: 1  // Siempre 1 — el backend crea un producto físico por cada entrada
            };
            return Array.from({ length: qty }, () => ({ ...base }));
          }),

          // Productos existentes (si los hay)
          existingProducts: existingProducts.map(item => ({
            productId: item.product.id_producto,
            productCode: item.product.cod_producto || undefined,
            productName: `${item.product.marca} ${item.product.color}`,
            inventoryType: item.product.inventoryType,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            total: item.total,
            isPreOrder: !deductInventory || (item.product.stock || 0) < item.quantity
          })),

          // Datos de la venta
          customerName: customer.name,
          customerEmail: customer.email || undefined,
          customerPhone: customer.phone || undefined,
          paymentMethod,
          payments,
          saleType,
          needsAdjustments,
          deductInventory,
          notes: isWholesaleMode ? 'COMPRA POR MAYOREO' : '',
          sellerCode: authData?.code || (isAdmin && adminSelectedSeller ? (adminSelectedSeller.codigo_vendedora || undefined) : undefined),
          sellerPassword: authData?.pass,
          discountPercentage: discountPercentage ? parseFloat(discountPercentage) : 0,
          discountReason,
          isLayaway,
          amountPaid: isLayaway ? Number(layawayAmount) : undefined,
          eventDate: eventDate || undefined,
          shippingCost,
          alterationCost
        };

        // Llamar endpoint especial para crear productos + venta
        const response = await saleService.createPreOrderSale(saleWithNewProductsData);
        setPendingSale(response.sale);
        toast.success(`✓ Venta registrada con ${tempProducts.length} producto(s) nuevo(s) creados`);

        // Continuar con el flujo normal
        if (needsAdjustments) {
          if (authData) {
            setTempSellerAuth(authData);
          }
          setShowTicketModal(true);
        } else {
          setSelectedSaleForTicket(response.sale);
          setShowTicketOptionsModal(true);
          resetForm();
          loadSales();
        }

        setLoading(false);
        return;
      }

      // 📦 FLUJO NORMAL: Solo productos existentes
      const saleData = {
        customerName: customer.name,
        customerEmail: customer.email || undefined,
        customerPhone: customer.phone || undefined,
        items: cart.map((item) => {
          return {
            productId: item.product.id_producto,
            productCode: item.product.cod_producto || undefined,
            productName: `${item.product.marca} ${item.product.color}`,
            inventoryType: item.product.inventoryType,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            total: item.total,
            isPreOrder: !deductInventory || (item.product.stock || 0) < item.quantity
          };
        }),
        paymentMethod,
        payments,
        saleType,
        needsAdjustments,
        deductInventory,
        notes: isWholesaleMode ? 'COMPRA POR MAYOREO' : '',
        sellerCode: authData?.code || (isAdmin && adminSelectedSeller ? (adminSelectedSeller.codigo_vendedora || undefined) : undefined),
        sellerPassword: authData?.pass,
        discountPercentage: discountPercentage ? parseFloat(discountPercentage) : 0,
        discountReason,
        isLayaway,
        amountPaid: isLayaway ? Number(layawayAmount) : undefined,
        eventDate: eventDate || undefined,
        shippingCost,
        alterationCost
      };

      const response = await saleService.createSale(saleData);
      setPendingSale(response.sale);

      const successMessage = saleType === 'SALE'
        ? 'Venta registrada exitosamente'
        : 'Renta registrada exitosamente';
      toast.success(successMessage);

      // Si necesita ajustes, mostrar modal de ticket
      if (needsAdjustments) {
        if (authData) {
          setTempSellerAuth(authData);
        }
        setShowTicketModal(true);
      } else {
        // En lugar de imprimir automáticamente, mostrar opciones
        setSelectedSaleForTicket(response.sale);
        setShowTicketOptionsModal(true);

        // Limpiar formulario
        resetForm();
        loadSales();
      }
    } catch (error: any) {
      console.error('Error al procesar venta:', error);
      toast.error(error.response?.data?.message || 'Error al procesar la venta');
    } finally {
      setLoading(false);
    }
  };

  // Crear ticket de ajustes
  const handleCreateTicket = async (ticketData: CreateTicketRequest) => {
    setLoading(true);

    try {
      const response = await ticketService.createTicket({
        ...ticketData,
        saleId: pendingSale?.id,
        sellerCode: tempSellerAuth?.code,
        sellerPassword: tempSellerAuth?.pass
      });

      // Vincular ticket con venta
      if (pendingSale) {
        await saleService.linkSaleToTicket(pendingSale.id, response.ticket.id);
      }

      toast.success('Ticket de ajuste creado exitosamente');



      // Mostrar opciones de ticket
      if (pendingSale) {
        // Actualizar el teléfono confirmado en el ticket de ajuste
        const updatedSale = {
          ...pendingSale,
          customerPhone: ticketData.customerPhone
        };
        setSelectedSaleForTicket(updatedSale);
        setShowTicketOptionsModal(true);
      }

      setShowTicketModal(false);
      resetForm();
      loadSales();
    } catch (error: any) {
      console.error('Error al crear ticket:', error);
      toast.error(error.response?.data?.message || 'Error al crear el ticket');
    } finally {
      setLoading(false);
    }
  };

  // Resetear formulario
  const resetForm = () => {
    // Limpiar formulario y nota de venta
    clearCart();
    setSaleType('SALE');
    setPaymentMethod('CASH');
    setIsLayaway(false);
    setLayawayAmount('');
    setPayments([{ method: 'CASH', amount: 0 }]);
    setPendingSale(null);
    setTempSellerAuth(null);
    setDiscountPercentage('');
    setDiscountReason('');
    setAdminSelectedSeller(null);
  };



  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'CASH': return 'Efectivo';
      case 'CARD': return 'Tarjeta';
      case 'TRANSFER': return 'Transferencia';
      case 'MERCADO_PAGO': return 'Mercado Pago';
      case 'KUESKI': return 'Kueski Pay';
      default: return method;
    }
  };


  const handleUpdateTicketPhone = async (sale: Sale, phone: string) => {
    // Si la venta tiene un ticket asociado, actualizar el teléfono en el ticket
    if (sale.ticket && sale.ticket.id) {
      try {
        await ticketService.updateTicket(sale.ticket.id, { customerPhone: phone });
      } catch (error) {
        console.error('Error al actualizar teléfono del ticket:', error);
        toast.error('Error al actualizar teléfono en el ticket');
      }
    }
  };

  const handlePrintTicket = async (sale: Sale, phone: string) => {
    try {
      setLoading(true);
      if (sale.customerPhone !== phone) {
        await handleUpdateTicketPhone(sale, phone);
      }

      const fullSaleData = await saleService.getSaleById(sale.id);

      printTicketWindow({ ...fullSaleData.sale, customerPhone: phone });
    } catch (error) {
      console.error('Error al preparar impresión:', error);
      toast.error('Error al cargar datos para imprimir');
    } finally {
      setLoading(false);
    }
  };


  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Validar datos básicos
      if (!newProductData.cod_producto || !newProductData.marca) {
        toast.error('Código y Proveedor son obligatorios');
        setLoading(false);
        return;
      }

      // ✅ Validar que la marca sea válida (debe existir en el catálogo)
      if (!existingBrands.includes(newProductData.marca)) {
        toast.error('Selecciona una marca válida de la lista');
        setLoading(false);
        return;
      }

      // ✅ MODO MÚLTIPLES VARIANTES (Color × Talla)
      if (createMultiple && selectedColors.length > 0 && selectedSizes.length > 0) {
        const variants: any[] = [];

        // Crear matriz de variantes
        selectedColors.forEach(color => {
          selectedSizes.forEach(size => {
            const tempProduct = {
              id_producto: `temp_${Date.now()}_${Math.random()}`, // ID temporal
              isTemp: true, // Marcar como temporal
              ...newProductData,
              color,
              talla: size,
              stock: 0,
              activo: false,
              pre_order: true,
              created_at: new Date(),
              updated_at: new Date()
            } as any;
            variants.push(tempProduct);
            addToCart(tempProduct);
          });
        });

        toast.success(`${variants.length} variantes agregadas a la nota (se crearán al confirmar venta)`);

        // Reset form y estados
        setShowProductModal(false);
        setNewProductData({
          cod_producto: '',
          color: '',
          marca: '',
          precio: 0,
          talla: '',
          stock: 0,
          activo: false,
          codigo_proveedor: '',
          type: 'maestro'
        });
        setSelectedColors([]);
        setSelectedSizes([]);
        setCreateMultiple(false);
        setLoading(false);
        return;
      }

      // ✅ MODO PRODUCTO INDIVIDUAL TEMPORAL (NO crear en DB aún)
      const tempProduct = {
        id_producto: `temp_${Date.now()}_${Math.random()}`, // ID temporal
        isTemp: true, // Marcar como temporal
        ...newProductData,
        stock: 0,
        activo: false,
        pre_order: true,
        created_at: new Date(),
        updated_at: new Date()
      } as any;

      // Agregar a la nota sin llamar API
      addToCart(tempProduct);
      toast.success('Producto agregado a la nota (se creará al confirmar venta)');

      // Reset form
      setShowProductModal(false);
      setNewProductData({
        cod_producto: '',
        color: '',
        marca: '',
        precio: 0,
        talla: '',
        stock: 0,

        activo: false,
        codigo_proveedor: '',
        type: 'maestro'
      });



    } catch (error: any) {
      console.error('Error creando producto:', error);
      toast.error(error.response?.data?.message || 'Error al crear producto');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppTicket = async (sale: Sale, phone: string) => {
    try {
      setLoading(true);

      // Actualizar el teléfono si es diferente
      if (sale.customerPhone !== phone) {
        await handleUpdateTicketPhone(sale, phone);
      }

      // Cargar los datos completos de la venta incluyendo items
      const fullSaleData = await saleService.getSaleById(sale.id);

      // Enviar por WhatsApp con los datos completos
      sendTicketWhatsApp({ ...fullSaleData.sale, customerPhone: phone });

      // Cerrar el modal
      setShowTicketOptionsModal(false);
      setSelectedSaleForTicket(null);
    } catch (error) {
      console.error('Error al preparar WhatsApp:', error);
      toast.error('Error al preparar el envío por WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección de flujo Pre-order
  const handlePreOrderFlowSelect = (flow: PreOrderFlow) => {
    setPreOrderFlow(flow);
    setShowPreOrderFlowModal(false);

    // Ajustar pagos según el flujo seleccionado
    if (flow === 'layaway') {
      // Activar modo apartado y establecer 50% como pago inicial
      setIsLayaway(true);
      setPayments([{ method: paymentMethod, amount: total * 0.5 }]);
      toast.success('✓ Apartado activado. Completa los datos y presiona "Procesar Venta"');
    } else if (flow === 'fullPayment') {
      // Desactivar apartado y establecer 100% como pago
      setIsLayaway(false);
      setPayments([{ method: paymentMethod, amount: total }]);
      toast.success('✓ Pago completo seleccionado. Completa los datos y presiona "Procesar Venta"');
    }

    // ✅ Modal solo establece el flujo de pago
    // Usuario continúa llenando el formulario normalmente
    // La venta se registra cuando presiona el botón "Procesar Venta"
  };

  return (
    <Layout>
      <div className="flex-1 w-full max-w-screen-2xl mx-auto flex flex-col h-full">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shrink-0">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Nota de Venta</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-2 text-sm"
              >
                <FileText size={16} />
                Historial
              </button>
              {!isAdmin && !isOnlineSeller && (
                <button
                  onClick={() => setIsSalesMode(!isSalesMode)}
                  className={`px-3 py-1.5 rounded-lg transition flex items-center gap-2 text-sm ${isSalesMode ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                >
                  {isSalesMode ? <Lock size={14} /> : <Unlock size={14} />}
                  {isSalesMode ? 'Modo Ventas' : 'Modo Libre'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden relative">
          {/* ═══════════════════════════════════════════════ */}
          {/* PANEL IZQUIERDO — DOCUMENTO NOTA DE VENTA      */}
          {/* ═══════════════════════════════════════════════ */}
          <div className="flex-1 overflow-y-auto bg-gray-100/50 p-4 lg:p-6">
            <div className="max-w-3xl mx-auto space-y-4">

              {/* ── SECCIÓN 1: ENCABEZADO ── */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 bg-gradient-to-r from-gray-800 to-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-white/80" />
                    <span className="text-sm font-bold text-white uppercase tracking-wider">Nueva Nota de Venta</span>
                  </div>
                  <span className="text-[10px] font-semibold bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full uppercase tracking-wider">Borrador</span>
                </div>

                <div className="p-4 space-y-4">
                  {/* Tipo de operación */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSaleType('SALE')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition ${saleType === 'SALE' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      VENTA
                    </button>
                    <button
                      onClick={() => setSaleType('RENT')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition ${saleType === 'RENT' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      RENTA
                    </button>
                  </div>

                  {/* Vendedora (solo admin) */}
                  {isAdmin && (
                    <div className="mb-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <label className="block text-sm font-bold text-gray-800 mb-1 flex items-center gap-2">
                        <User size={16} />
                        Seleccionar Vendedora (Admin)
                      </label>
                      <select
                        value={adminSelectedSeller?.id_usuario || ''}
                        onChange={(e) => {
                          const seller = sellersList.find(s => s.id_usuario === e.target.value);
                          setAdminSelectedSeller(seller || null);
                        }}
                        className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 bg-white text-gray-900"
                      >
                        <option value="">-- Selecciona una vendedora --</option>
                        {sellersList.map(s => (
                          <option key={s.id_usuario} value={s.id_usuario}>
                            {s.nombre_completo || s.username} ({s.codigo_vendedora})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* ── SECCIÓN 2: DATOS DE LA CLIENTA ── */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <User size={14} />
                  Datos de la Clienta
                </h3>

                {/* Búsqueda rápida */}
                <div className="relative mb-3" ref={searchContainerRef}>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                  <input
                    type="text"
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    placeholder="Buscar cliente existente..."
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50"
                  />
                  {isSearchingCustomer && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showCustomerOptions && customerOptions.length > 0 && (
                    <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {customerOptions.map((cust) => (
                        <div key={cust.id} className="p-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0" onClick={() => selectCustomer(cust)}>
                          <p className="font-semibold text-sm text-gray-800">{cust.fullName}</p>
                          <p className="text-xs text-gray-500">{cust.phone} {cust.email && `• ${cust.email}`}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cliente *</label>
                    <input type="text" placeholder="Nombre" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Evento</label>
                    <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono</label>
                    <input type="text" placeholder="Número" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" />
                  </div>
                </div>
              </div>

              {/* ── SECCIÓN 3: ARTÍCULOS ── */}
              <div className={`bg-white rounded-xl shadow-sm border overflow-hidden ${isWholesaleMode ? 'border-purple-300 ring-1 ring-purple-100' : 'border-gray-200'}`}>
                <div className={`px-4 py-3 border-b flex items-center justify-between ${isWholesaleMode ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-4">
                    <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${isWholesaleMode ? 'text-purple-700' : 'text-gray-500'}`}>
                      <Package size={14} />
                      Artículos ({cart.length})
                    </h3>
                    
                    {/* TOGGLE MAYOREO */}
                    <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-md shadow-sm border border-gray-200">
                      <div 
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isWholesaleMode ? 'bg-purple-600' : 'bg-gray-200'}`}
                        onClick={() => {
                          const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
                          if (!isWholesaleMode) {
                              if (customer?.type === 'MAYORISTA' || totalItems >= 12 || isAdmin) {
                                  setIsWholesaleMode(true);
                                  toast.success('Modo Mayoreo Activado');
                              } else {
                                  toast.error('Requiere Cliente Mayorista o 12+ piezas en la nota');
                              }
                          } else {
                              setIsWholesaleMode(false);
                          }
                        }}
                      >
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isWholesaleMode ? 'translate-x-2' : '-translate-x-2'}`} />
                      </div>
                      <span className="text-xs font-bold text-gray-700">Modo Mayoreo</span>
                    </div>

                  </div>
                  {cart.length > 0 && (
                    <button onClick={clearCart} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                      <Trash2 size={12} /> Limpiar
                    </button>
                  )}
                </div>

                {cart.length === 0 ? (
                  <div className="p-10 text-center">
                    <FileText size={48} className={`mx-auto mb-3 ${isWholesaleMode ? 'text-purple-200' : 'text-gray-200'}`} />
                    <p className={`font-medium ${isWholesaleMode ? 'text-purple-400' : 'text-gray-400'}`}>Sin artículos en la nota</p>
                    <p className="text-xs text-gray-300 mt-1">Agrega productos desde el panel lateral →</p>
                  </div>
                ) : (
                  <div>
                    {/* Header de tabla */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      <div className="col-span-1">#</div>
                      <div className="col-span-5">Descripción del Artículo</div>
                      <div className="col-span-1 text-center">Cant.</div>
                      <div className="col-span-2 text-right">Precio</div>
                      <div className="col-span-2 text-right">Importe</div>
                      <div className="col-span-1"></div>
                    </div>
                    {cart.map((item, idx) => {
                      const isPreOrder = (item.product.stock || 0) < item.quantity || item.product.pre_order;
                      return (
                        <div key={item.product.id_producto} className="grid grid-cols-12 gap-2 px-4 py-3 items-start border-b border-gray-100 group hover:bg-blue-50/30 transition-colors">
                          {/* # */}
                          <div className="col-span-1 text-xs text-gray-400 font-mono pt-0.5">{idx + 1}</div>

                          {/* Descripción completa */}
                          <div className="col-span-5 min-w-0">
                            <p className="text-sm font-bold text-gray-900 font-mono">{item.product.cod_producto || item.product.marca}</p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-[11px]">
                              <span><span className="text-gray-400">Marca:</span> <span className="text-gray-700">{item.product.marca || '—'}</span></span>
                              <span><span className="text-gray-400">Talla:</span> <span className="text-gray-700">{item.product.talla || '—'}</span></span>
                              <span><span className="text-gray-400">Color:</span> <span className="text-gray-700">{item.product.color || '—'}</span></span>
                            </div>
                            {/* Estado */}
                            <div className="mt-1 flex items-center gap-1.5">
                              {isPreOrder ? (
                                <span className="text-[10px] bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-bold">PRE-ORDER</span>
                              ) : (
                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">✓ En stock</span>
                              )}
                              {item.product.inventoryType && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${item.product.inventoryType === 'novias' ? 'bg-pink-50 text-pink-600' : 'bg-blue-50 text-blue-600'}`}>
                                  {item.product.inventoryType === 'novias' ? 'NOVIAS' : 'MAESTRO'}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Cantidad */}
                          <div className="col-span-1 flex flex-col items-center gap-0.5 pt-0.5">
                            <button onClick={() => handleUpdateQuantity(item.product.id_producto, item.quantity + 1)} className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-xs leading-none">+</button>
                            <span className="text-sm font-bold text-gray-800">{item.quantity}</span>
                            <button onClick={() => handleUpdateQuantity(item.product.id_producto, item.quantity - 1)} className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-xs leading-none">−</button>
                          </div>

                          {/* Precio unitario */}
                          <div className="col-span-2 text-right pt-0.5 mt-1">
                            {isWholesaleMode ? (
                              <div className="flex flex-col items-end">
                                <span className="text-[10px] text-gray-400 line-through leading-none">{formatCurrency(Number(item.product.precio) || 0)}</span>
                                <span className="text-sm font-bold text-gray-900 leading-tight">{formatCurrency(item.unitPrice)}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">{formatCurrency(item.unitPrice)}</span>
                            )}
                          </div>

                          {/* Importe */}
                          <div className="col-span-2 text-right pt-0.5">
                            <span className="text-sm font-bold text-gray-900">{formatCurrency(item.total)}</span>
                          </div>

                          {/* Eliminar */}
                          <div className="col-span-1 flex justify-center pt-0.5">
                            <button onClick={() => handleRemoveFromCart(item.product.id_producto)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity p-1 rounded hover:bg-red-50" title="Quitar artículo">
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── SECCIÓN 4: RESUMEN DE LA NOTA ── */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Resumen de la Nota</h3>
                </div>
                <div className="p-4 space-y-2 text-sm">
                  {/* Subtotal */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal ({cart.length} {cart.length === 1 ? 'artículo' : 'artículos'})</span>
                    <span className="font-medium text-gray-800">{formatCurrency(grossTotal)}</span>
                  </div>

                  {/* Descuento */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 shrink-0">Descuento</span>
                      <input type="number" value={discountPercentage} onChange={(e) => setDiscountPercentage(e.target.value)} disabled={isWholesaleMode} placeholder="0" className="w-14 px-2 py-1 text-xs border border-gray-200 rounded text-center focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400" />
                      <span className="text-gray-400 text-xs">%</span>
                      {discountPercentage && !isWholesaleMode && (
                        <input type="text" value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} placeholder="Motivo del descuento" className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
                      )}
                      {discountVal > 0 && <span className="text-red-500 font-medium ml-auto">-{formatCurrency(discountVal)}</span>}
                    </div>
                    {isWholesaleMode && (
                      <span className="text-xs text-orange-600 font-semibold mt-1">El descuento de Mayoreo (22%) ya fue aplicado directamente al precio unitario de las prendas.</span>
                    )}
                  </div>

                  {/* Envío y Arreglos */}
                  {(isOnlineSeller || isAdmin) && (
                    <>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">Costo de envío</span>
                        </div>
                        <div className="relative w-28">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input type="number" value={shippingCost} onChange={(e) => setShippingCost(parseFloat(e.target.value) || 0)} className="w-full pl-5 pr-2 py-1 text-xs border border-gray-200 rounded text-right focus:ring-1 focus:ring-blue-500" />
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">Arreglos / Costuras</span>
                        </div>
                        <div className="relative w-28">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input type="number" value={alterationCost} onChange={(e) => setAlterationCost(parseFloat(e.target.value) || 0)} className="w-full pl-5 pr-2 py-1 text-xs border border-gray-200 rounded text-right focus:ring-1 focus:ring-blue-500" />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Ajustes modista */}
                  <label className="flex items-center gap-2 text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={needsAdjustments} onChange={(e) => setNeedsAdjustments(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <Scissors size={14} />
                    <span className="text-xs">Requiere ajustes de modista</span>
                  </label>

                  {/* ═══ TOTAL GRANDE ═══ */}
                  <div className="mt-3 pt-3 border-t-2 border-gray-800">
                    <div className="flex justify-between items-center bg-gray-900 text-white rounded-lg px-4 py-3">
                      <span className="text-lg font-bold">TOTAL A PAGAR</span>
                      <span className="text-2xl font-black">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── SECCIÓN 5: FORMA DE PAGO ── */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">¿Cómo paga la clienta?</h3>
                </div>
                <div className="p-4">
                  {/* Apartado toggle */}
                  {saleType === 'SALE' && (
                    <div className={`mb-4 rounded-lg border-2 transition-all ${isLayaway ? 'bg-purple-50 border-purple-300' : 'border-gray-200'}`}>
                      <div
                        className={`flex items-center justify-between p-3 select-none cursor-pointer`}
                        onClick={() => {
                          const val = !isLayaway;
                          setIsLayaway(val);
                          if (!val) setLayawayAmount('');
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-10 h-5 flex items-center rounded-full p-0.5 transition-colors ${isLayaway ? 'bg-purple-600' : 'bg-gray-300'}`}>
                            <div className={`bg-white w-4 h-4 rounded-full shadow transform transition-transform ${isLayaway ? 'translate-x-5' : ''}`}></div>
                          </div>
                          <span className={`text-sm font-semibold ${isLayaway ? 'text-purple-800' : 'text-gray-600'}`}>¿Es un apartado?</span>
                          <span className="text-xs text-gray-400">(pago parcial)</span>
                        </div>
                        {isLayaway && <Lock size={14} className="text-purple-400" />}
                      </div>

                      {isLayaway && (
                        <div className="px-3 pb-3">
                          {cart.some(item => (item.product.stock || 0) < item.quantity) && (
                            <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800 flex items-center gap-2">
                              <AlertCircle size={14} className="shrink-0" />
                              PRE-ORDER — Se recomienda anticipo del 50%
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-purple-700 shrink-0">Anticipo inicial:</label>
                            <div className="relative flex-1">
                              <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold text-sm">$</span>
                              <input
                                type="number" value={layawayAmount} readOnly={payments.length > 1}
                                onChange={(e) => { setLayawayAmount(e.target.value); const val = parseFloat(e.target.value) || 0; if (payments.length <= 1) setPayments([{ method: payments[0]?.method || paymentMethod, amount: val }]); }}
                                className={`w-full pl-7 pr-3 py-2 text-sm font-bold border rounded-lg ${payments.length > 1 ? 'border-gray-200 cursor-not-allowed text-gray-400' : 'border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'}`}
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Métodos de pago */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Método de pago</p>
                    {payments.map((p, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <select
                          value={p.method}
                          onChange={(e) => { const np = [...payments]; np[idx].method = e.target.value as PaymentMethod; setPayments(np); if (idx === 0) setPaymentMethod(e.target.value as PaymentMethod); }}
                          className="w-1/3 px-2 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 text-sm bg-white"
                        >
                          <option value="CASH">Efectivo</option>
                          <option value="CARD">Tarjeta</option>
                          <option value="TRANSFER">Transferencia</option>
                          {(user?.rol_usr === UserRole.ADMIN || user?.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                            <>
                              <option value="MERCADO_PAGO">Mercado Pago</option>
                              <option value="KUESKI">Kueski Pay</option>
                            </>
                          )}
                        </select>
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number" value={p.amount}
                            onChange={(e) => { const np = [...payments]; np[idx].amount = parseFloat(e.target.value) || 0; setPayments(np); }}
                            className="w-full pl-6 pr-2 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 text-sm" placeholder="Monto"
                          />
                        </div>
                        {payments.length > 1 && (
                          <button onClick={() => setPayments(payments.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => setPayments([...payments, { method: 'CASH', amount: 0 }])} className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1">
                      + Agregar otro método de pago
                    </button>
                  </div>

                  {/* ═══ RESUMEN DE PAGO ═══ */}
                  <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total de la nota</span>
                      <span className="font-medium text-gray-800">{formatCurrency(total)}</span>
                    </div>
                    {/* Mostrar desglose si hay extras o descuentos para que sea sumamente claro */}
                    {(shippingCost > 0 || alterationCost > 0 || discountVal > 0) && (
                      <div className="flex flex-col gap-1 text-xs text-gray-500 pl-3 border-l-2 border-gray-200 ml-1 mb-2 mt-1">
                        <div className="flex justify-between">
                          <span>Subtotal (prendas)</span>
                          <span className="text-gray-600">{formatCurrency(grossTotal)}</span>
                        </div>
                        {discountVal > 0 && (
                          <div className="flex justify-between">
                            <span>Descuento</span>
                            <span className="text-red-500">-{formatCurrency(discountVal)}</span>
                          </div>
                        )}
                        {shippingCost > 0 && (
                          <div className="flex justify-between">
                            <span>Costo de envío</span>
                            <span>+{formatCurrency(shippingCost)}</span>
                          </div>
                        )}
                        {alterationCost > 0 && (
                          <div className="flex justify-between">
                            <span>Arreglos / Costuras</span>
                            <span>+{formatCurrency(alterationCost)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Pagado por la clienta</span>
                      <span className={`font-bold ${totalPaid >= total ? 'text-green-600' : 'text-blue-600'}`}>{formatCurrency(totalPaid)}</span>
                    </div>
                    {/* Saldo pendiente */}
                    {total - totalPaid > 0.01 && (
                      <div className="flex justify-between items-center bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-sm">
                        <span className="text-orange-800 font-semibold">Saldo pendiente</span>
                        <span className="font-black text-orange-700 text-lg">{formatCurrency(total - totalPaid)}</span>
                      </div>
                    )}
                    {Math.abs(totalPaid - total) < 0.01 && total > 0 && (
                      <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
                        <span className="text-green-700 font-semibold">✓ Pago completo</span>
                        <span className="font-bold text-green-600">{formatCurrency(totalPaid)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── SECCIÓN 6: ACCIÓN ── */}
              <div className="flex gap-3">
                <button onClick={handleClearCart} className="px-5 py-3 bg-gray-100 text-gray-600 rounded-xl font-semibold text-sm hover:bg-red-50 hover:text-red-600 transition flex items-center gap-2 border border-gray-200">
                  <Trash2 size={16} /> Limpiar Todo
                </button>
                <button
                  onClick={handleProcessSale}
                  disabled={loading || cart.length === 0}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold text-base hover:bg-blue-700 transition disabled:opacity-50 shadow-md flex items-center justify-center gap-2"
                >
                  <FileText size={18} />
                  {loading ? 'Procesando...' : (saleType === 'SALE' ? '✓ Registrar Nota de Venta' : '✓ Registrar Renta')}
                </button>
              </div>

            </div>{/* end max-w-3xl */}
          </div>{/* end left panel */}

          {/* ═══════════════════════════════════════════════ */}
          {/* PANEL DERECHO — BUSCADOR DE PRODUCTOS           */}
          {/* ═══════════════════════════════════════════════ */}
          <div className={`
            fixed inset-0 z-50 bg-white transform transition-transform duration-300 ease-in-out
            lg:transform-none lg:static lg:z-auto lg:w-[320px] xl:w-[360px] lg:border-l border-gray-200
            flex flex-col lg:h-full shrink-0 shadow-xl lg:shadow-none
            ${isMobileCartOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'}
          `}>
            {/* Mobile header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 lg:hidden">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Search size={16} /> Buscar Productos
              </h3>
              <button onClick={() => setIsMobileCartOpen(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            {/* Desktop header */}
            <div className="hidden lg:flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
              <Package size={16} className="text-gray-500" />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Productos</span>
            </div>

            {/* Search */}
            <div className="p-3 border-b border-gray-100 space-y-2">
              <div className="relative" ref={productSearchRef}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                <input
                  type="text"
                  placeholder="Buscar marca, color, código... (Enter)"
                  value={searchTerm}
                  onChange={(e) => {
                    const normalizedTerm = e.target.value.replace(/'/g, '-').replace(/\?/g, '_').toUpperCase();
                    setSearchTerm(normalizedTerm);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
              <div className="flex gap-2">
                {!isOnlineSeller && (
                  <button onClick={() => setShowScanner(true)} className="flex-1 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200 flex items-center justify-center gap-1">
                    <QrCode size={14} /> Escáner
                  </button>
                )}
                <button onClick={() => setShowProductModal(true)} className="flex-1 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100 flex items-center justify-center gap-1">
                  <Plus size={14} /> Pedir Producto
                </button>
              </div>
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                </div>
              )}

              {!loading && filteredProducts.length === 0 && (
                <div className="text-center py-8">
                  <Package size={32} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-xs text-gray-400">No se encontraron productos</p>
                </div>
              )}

              {!loading && filteredProducts.map((product) => (
                <div
                  key={product.id_producto}
                  onClick={() => handleAddToCart(product)}
                  className="px-3 py-2.5 border-b border-gray-50 hover:bg-blue-50/50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-bold text-gray-900 font-mono">{product.cod_producto || '—'}</span>
                        {product.inventoryType && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded leading-none ${product.inventoryType === 'novias' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                            {product.inventoryType === 'novias' ? 'NOVIAS' : 'MAESTRO'}
                          </span>
                        )}
                        <span className={`text-[10px] font-semibold ${(product.stock || 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          Stock: {product.stock || 0}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px]">
                        <span><span className="text-gray-400">Marca:</span> <span className="text-gray-700 font-medium">{product.marca || '—'}</span></span>
                        <span><span className="text-gray-400">Talla:</span> <span className="text-gray-700 font-medium">{product.talla || '—'}</span></span>
                        <span><span className="text-gray-400">Color:</span> <span className="text-gray-700 font-medium">{product.color || '—'}</span></span>
                      </div>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end">
                      <p className="text-sm font-bold text-blue-600">{formatCurrency(Number(product.precio))}</p>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                        <Plus size={14} className="text-blue-500" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* External scanner indicator */}
            {!isOnlineSeller && (
              <div className="hidden lg:flex items-center justify-center p-2 border-t border-gray-100">
                <span className="text-[10px] text-blue-600 font-medium bg-blue-50 px-3 py-1 rounded-full animate-pulse">
                  Lector Externo Activo
                </span>
              </div>
            )}
          </div>

          {/* Mobile floating button to open product search */}
          {!isMobileCartOpen && (
            <button
              onClick={() => setIsMobileCartOpen(true)}
              className="lg:hidden fixed bottom-6 right-6 z-40 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 transition"
            >
              <Plus size={24} />
            </button>
          )}

        </div>{/* end flex row */}

        {/* Historial de ventas - Modal */}
        {showHistory && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowHistory(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Header del modal */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Historial de Notas</h2>
                  <p className="text-sm text-gray-600 mt-1">Total de ventas encontrados: {totalCount}</p>
                </div>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Filtros */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Búsqueda Inteligente Global</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={globalSearch}
                        onChange={(e) => setGlobalSearch(e.target.value)}
                        placeholder="Buscar por código, folio, color, talla, cliente..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 shadow-sm font-medium"
                      />
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                    <CustomCombobox
                      value={filterCustomer}
                      onChange={setFilterCustomer}
                      options={allCustomerNames}
                      placeholder="Buscar Cliente..."
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendedora</label>
                    <CustomCombobox
                      value={filterSeller}
                      onChange={setFilterSeller}
                      options={allSellerNames}
                      placeholder="Buscar Vendedora..."
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
                    <select
                      value={filterPaymentMethod}
                      onChange={(e) => setFilterPaymentMethod(e.target.value as PaymentMethod | 'ALL')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    >
                      <option value="ALL">Todos</option>
                      <option value="CASH">Efectivo</option>
                      <option value="CARD">Tarjeta</option>
                      <option value="TRANSFER">Transferencia</option>
                      <option value="MERCADO_PAGO">Mercado Pago</option>
                      <option value="KUESKI">Kueski Pay</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                    >
                      <option value="ALL">Todos</option>
                      <option value="COMPLETADA">Completada</option>
                      <option value="PRE-ORDER">PRE-ORDER</option>
                    </select>
                  </div>
                </div>
                {(filterCustomer || filterDateFrom || filterDateTo || filterPaymentMethod !== 'ALL' || filterStatus !== 'ALL' || filterSeller || globalSearch) && (
                  <button
                    onClick={() => {
                      setFilterCustomer('');
                      setGlobalSearch('');
                      setFilterDateFrom('');
                      setFilterDateTo('');
                      setFilterPaymentMethod('ALL');
                      setFilterStatus('ALL');
                      setFilterSeller('');
                    }}
                    className="mt-4 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>

              {/* Tabla de ventas */}
              {/* Tabla de ventas */}
              <div className="flex-1 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white border-b-2 border-gray-200 z-10 shadow-sm">
                    <tr>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Folio</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Fecha</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Cliente</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Vendedora</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Código</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Marca</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Color</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Talla</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Tipo</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Total</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Pagos</th>
                      <th className="text-left py-2 px-3 font-bold text-gray-700">Estado</th>
                      <th className="text-center py-2 px-3 font-bold text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allSales.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center py-12 text-gray-500">
                          <FileText size={48} className="mx-auto mb-3 opacity-50" />
                          <p className="font-medium">{loading ? 'Cargando ventas...' : 'No hay ventas que coincidan con los filtros'}</p>
                        </td>
                      </tr>
                    ) : (
                      allSales.map((sale) => {
                        const totalPaid = sale.payments?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;
                        // Consideramos no liquidado si el pago es menor al total y no es una venta cancelada o renta completada?
                        // Nos enfocamos en el monto.
                        const isNotLiquidated = sale.status !== 'CANCELLED' && (Math.round(totalPaid) < Math.round(sale.total) || ['PENDIENTE', 'APARTADO', 'PRE-ORDER'].includes(sale.status));

                        return (
                          <tr key={sale.id} className={`border-b ${isNotLiquidated ? 'bg-red-50/50 hover:bg-red-100 border-red-100' : 'border-gray-100 hover:bg-blue-50'} transition group ${sale.parentSaleId ? 'bg-gray-50/30' : ''}`}>
                            <td className={`py-2 px-3 font-bold text-gray-800 ${sale.parentSaleId ? 'pl-4' : ''}`}>
                              <div className="flex items-center gap-1">
                                {sale.parentSaleId && (
                                  <div className="flex flex-col">
                                    <div className="flex items-center">
                                      <CornerDownRight size={14} className="text-blue-500 mr-1" />
                                      <span className="text-[10px] text-blue-600 font-black tracking-tighter leading-none">{sale.saleType || 'PAGO'}</span>
                                    </div>
                                    <span className="text-[9px] text-gray-400 ml-4 font-normal">REF: {sale.parentSale?.folio || `#${sale.parentSaleId}`}</span>
                                  </div>
                                )}
                                {isNotLiquidated && !sale.parentSaleId && (
                                  <div title="Venta con saldo pendiente">
                                    <AlertCircle size={14} className="text-red-500" />
                                  </div>
                                )}
                                <span className={sale.parentSaleId ? 'text-xs font-bold text-gray-700 ml-1' : ''}>
                                  {sale.folio || sale.id}
                                </span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-gray-900">
                              {new Date(sale.createdAt).toLocaleDateString('es-MX', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit'
                              })}
                            </td>
                            <td className="py-2 px-3">
                              <div className="font-medium text-gray-900">{sale.customerName}</div>
                              <div className="text-xs text-gray-500">{sale.customerPhone || '-'}</div>
                            </td>

                            {/* Vendedora */}
                            <td className="py-2 px-3 text-gray-700 max-w-[150px] truncate" title={sale.seller?.username || 'N/A'}>
                              {sale.seller?.username || 'N/A'}
                            </td>

                            {/* Código */}
                            <td className="py-2 px-3 text-gray-700">
                              {(sale.items?.length > 0 ? sale.items : (sale.parentSale as any)?.items || []).map((item: any, i: number) => (
                                <div key={i} className="whitespace-nowrap text-xs">
                                  {item.product?.cod_producto || item.productCode || '-'}
                                </div>
                              ))}
                            </td>

                            {/* Marca */}
                            <td className="py-2 px-3 text-gray-700">
                              {(sale.items?.length > 0 ? sale.items : (sale.parentSale as any)?.items || []).map((item: any, i: number) => (
                                <div key={i} className="whitespace-nowrap text-xs">
                                  {item.product?.marca || '-'}
                                </div>
                              ))}
                            </td>

                            {/* Color */}
                            <td className="py-2 px-3 text-gray-700">
                              {sale.items?.map((item: any, i) => (
                                <div key={i} className="whitespace-nowrap">
                                  {item.product?.color || '-'}
                                </div>
                              ))}
                            </td>

                            {/* Talla */}
                            <td className="py-2 px-3 text-gray-700">
                              {sale.items?.map((item: any, i) => (
                                <div key={i} className="whitespace-nowrap font-semibold">
                                  {item.product?.talla || '-'}
                                </div>
                              ))}
                            </td>

                            <td className="py-2 px-3 text-gray-900">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${sale.saleType === 'RENT'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : sale.saleType === 'ABONO'
                                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                                  : sale.saleType === 'LIQUIDACION'
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-green-50 text-green-700 border-green-200'
                                }`}>
                                {sale.saleType === 'RENT' ? 'RENTA' : sale.saleType === 'ABONO' ? 'ABONO' : sale.saleType === 'LIQUIDACION' ? 'LIQUIDACIÓN' : 'VENTA'}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <div className="font-bold text-blue-600">
                                {formatCurrency(sale.total)}
                              </div>
                              {((sale.shippingCost || 0) > 0 || (sale.alterationCost || 0) > 0) && (
                                <div className="text-[10px] text-gray-500 mt-0.5 flex flex-col font-medium">
                                  {(sale.shippingCost || 0) > 0 && <span>+ Envío: {formatCurrency(sale.shippingCost)}</span>}
                                  {(sale.alterationCost || 0) > 0 && <span>+ Arreglos: {formatCurrency(sale.alterationCost)}</span>}
                                </div>
                              )}
                            </td>
                            <td className="py-2 px-3 text-gray-900 text-xs">
                              <div className="flex flex-col gap-1">
                                {sale.paymentMethod === 'Mixto' && sale.payments && sale.payments.length > 0 ? (
                                  <div className="flex flex-col">
                                    {sale.payments.length > 2 ? (
                                      <span className="font-medium text-purple-600">Mixto ({sale.payments.length} abonos)</span>
                                    ) : (
                                      sale.payments.map((p: any, idx: number) => (
                                        <span key={idx} className="whitespace-nowrap text-[10px] text-gray-600">
                                          {getPaymentMethodLabel(p.method)}: {formatCurrency(Number(p.amount))}
                                        </span>
                                      ))
                                    )}
                                  </div>
                                ) : (
                                  <span className="font-medium">{getPaymentMethodLabel(sale.paymentMethod)}</span>
                                )}
                                {sale.amountPaid !== null && sale.amountPaid !== undefined && (
                                  <div className="flex flex-col mt-1 pt-1 border-t border-gray-100 bg-white/50 rounded p-1">
                                    {(sale.total - sale.amountPaid) <= 0.01 ? (
                                      <div className="bg-green-600 text-white font-black text-[10px] px-2 py-1 rounded text-center border border-green-700 shadow-sm">
                                        LIQUIDADO
                                      </div>
                                    ) : (
                                      <>
                                        <span className="text-green-600 font-bold text-[9px] flex justify-between">
                                          <span>Abonado:</span>
                                          <span>{formatCurrency(sale.amountPaid)}</span>
                                        </span>
                                        <span className="text-red-600 font-bold text-[9px] flex justify-between">
                                          <span>Pendiente:</span>
                                          <span>{formatCurrency(Math.max(0, sale.total - (sale.amountPaid || 0)))}</span>
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex flex-col gap-1">
                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${sale.status === 'COMPLETADA' || sale.status === 'COMPLETED'
                                  ? 'bg-green-100 text-green-700'
                                  : sale.status === 'PRE-ORDER'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : sale.status === 'APARTADO'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}>
                                  {sale.status === 'PRE-ORDER' ? 'PRE-ORDER' : sale.status}
                                </span>
                                {sale.status === 'APARTADO' && (sale as any).isPreOrder && (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-600 border border-amber-200 text-center">
                                    PRE-ORDER
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-center">
                              <div className="flex justify-center gap-1">
                                <button
                                  onClick={() => {
                                    setSelectedSaleForDetail(sale);
                                    setShowSaleDetailModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded"
                                  title="Ver Detalle"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedSaleForTicket(sale);
                                    setShowTicketOptionsModal(true);
                                  }}
                                  className="text-gray-600 hover:text-gray-800 p-1.5 hover:bg-gray-100 rounded"
                                  title="Opciones"
                                >
                                  <Printer size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer con totales y paginación */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    Mostrando {allSales.length} de {totalCount} venta(s)
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={currentPage === 1 || loading}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition"
                    >
                      Anterior
                    </button>
                    <span className="text-sm font-medium text-gray-700">Página {currentPage} de {Math.max(1, Math.ceil(totalCount / itemsPerPage))}</span>
                    <button
                      disabled={currentPage >= Math.ceil(totalCount / itemsPerPage) || loading}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>

                {/* Se solicitaron ocultar estos totales
                {isAdmin && (
                  <div className="flex flex-wrap gap-4 md:gap-8 justify-end text-right mt-4 md:mt-0">
                    <div>
                      <p className="text-xs text-blue-600 uppercase tracking-wide font-bold">Página actual</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(allSales.reduce((sum, sale) => sum + (sale.total || 0), 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600 uppercase tracking-wide font-bold">Total del Mes</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(totalMonth)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600 uppercase tracking-wide font-bold">Total General (Filtros)</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(totalGeneral)}
                      </p>
                    </div>
                  </div>
                )}
                */}
              </div>
            </div>
          </div>
        )
        }

        {/* Sale Detail Modal */}
        {
          showSaleDetailModal && selectedSaleForDetail && (
            <SaleDetailModal
              sale={selectedSaleForDetail}
              onClose={() => {
                setShowSaleDetailModal(false);
                setSelectedSaleForDetail(null);
              }}
              onSaleUpdated={() => loadSales()}
            />
          )
        }

        {/* Floating Cart Button for Mobile */}
        <div className={`lg:hidden fixed bottom-6 right-6 z-40 transition-transform duration-300 ${isMobileCartOpen ? 'translate-y-24' : 'translate-y-0'}`}>
          <button
            onClick={() => setIsMobileCartOpen(true)}
            className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition flex items-center gap-2 relative"
          >
            <ShoppingCart size={24} />
            <span className="font-bold">{cart.length}</span>
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500"></span>
              </span>
            )}
          </button>
        </div>
      </div >

      {/* Modal de confirmación de inventario */}
      {
        showInventoryModal && (
          <InventoryModal
            onConfirm={(deduct) => {
              if (isAdmin) {
                if (!adminSelectedSeller) {
                  toast.error('Selecciona una vendedora primero');
                  setShowInventoryModal(false);
                  return;
                }
                completeSale(deduct);
              } else if (isSalesMode && !isOnlineSeller) {
                setPendingDeductInventory(deduct);
                setShowInventoryModal(false);
                setShowAuthModal(true);
              } else {
                completeSale(deduct);
              }
            }}
            onClose={() => setShowInventoryModal(false)}
          />
        )
      }

      {/* Modal de Autenticación de Vendedora */}
      {
        showAuthModal && (
          <SalesAuthModal
            onConfirm={(code, pass) => completeSale(pendingDeductInventory, { code, pass })}
            onClose={() => setShowAuthModal(false)}
            loading={loading}
          />
        )
      }

      {/* Modal de crear ticket */}
      {
        showTicketModal && (
          <TicketModal
            customerName={customer.name}
            customerPhone={customer.phone}
            customerEmail={customer.email}
            cartItems={cart}
            onSubmit={handleCreateTicket}
            onClose={() => {
              setShowTicketModal(false);
              resetForm();
              loadSales();
            }}
            loading={loading}
          />
        )
      }

      {/* Modal de Opciones de Recibo (Imprimir/WhatsApp) */}
      {
        showTicketOptionsModal && selectedSaleForTicket && (
          <TicketOptionsModal
            sale={selectedSaleForTicket!}
            onPrint={(phone) => handlePrintTicket(selectedSaleForTicket!, phone)}
            onWhatsApp={(phone) => handleWhatsAppTicket(selectedSaleForTicket!, phone)}
            onClose={() => {
              setShowTicketOptionsModal(false);
              setSelectedSaleForTicket(null);
            }}
          />
        )
      }

      {/* Modal Crear Producto (Pedido Especial) */}
      {
        showProductModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowProductModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 animate-fadeIn" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Solicitar Nuevo Producto</h2>
              <p className="mb-4 text-sm text-gray-600">
                Este producto se registrará como <strong>PRE-ORDER</strong> ya que no está en stock.
              </p>

              <form onSubmit={handleCreateProduct} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código de Producto *</label>
                    <CustomCombobox
                      value={newProductData.cod_producto}
                      onChange={(val) => {
                        const found = codeBrands.find(cb => cb.code.toLowerCase() === val.toLowerCase());
                        setNewProductData({
                          ...newProductData,
                          cod_producto: val,
                          marca: found ? found.brand : newProductData.marca
                        });
                      }}
                      options={existingCodes}
                      placeholder="Ej: NUEVO-001"
                      className="w-full"
                      allowCustom={true}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor (Marca) *</label>
                    <CustomCombobox
                      value={newProductData.marca || ''}
                      onChange={(val) => setNewProductData({ ...newProductData, marca: val, color: '' })}
                      options={existingBrands}
                      placeholder="Seleccionar Proveedor..."
                      className="w-full"
                      allowCustom={false}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Inventario</label>
                  <select
                    value={newProductData.type || 'maestro'}
                    onChange={e => setNewProductData({ ...newProductData, type: e.target.value as 'maestro' | 'novias' })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="maestro">Inventario Maestro (General)</option>
                    <option value="novias">Inventario Novias</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <CustomCombobox
                      value={newProductData.color || ''}
                      onChange={(val) => setNewProductData({ ...newProductData, color: val })}
                      options={newProductData.marca
                        ? Array.from(new Set(brandColors.filter(bc => bc.brand === newProductData.marca).map(bc => bc.color.toUpperCase().trim())))
                        : []}
                      placeholder="Ej: ROJO"
                      className="w-full"
                      allowCustom={true}  // Permitir crear nuevos colores
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Talla</label>
                    <CustomCombobox
                      value={newProductData.talla || ''}
                      onChange={val => setNewProductData({ ...newProductData, talla: val })}
                      options={newProductData.marca
                        ? Array.from(new Set(brandSizes.filter(bs => bs.brand === newProductData.marca).map(bs => bs.size.toUpperCase().trim())))
                        : []}
                      placeholder="Ej: M"
                      className="w-full"
                      allowCustom={true}
                    />
                  </div>
                </div>

                {/* Sección de Variantes Múltiples */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createMultiple}
                      onChange={e => setCreateMultiple(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="font-medium text-gray-700">Crear múltiples variantes (Color × Talla)</span>
                  </label>

                  {createMultiple && (
                    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
                      {/* Sección Colores */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Colores:</label>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                          {(newProductData.marca ? brandColors.filter(bc => bc.brand === newProductData.marca).map(bc => bc.color) : []).map(color => (
                            <label key={color} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={selectedColors.includes(color)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedColors([...selectedColors, color]);
                                  } else {
                                    setSelectedColors(selectedColors.filter(c => c !== color));
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <span>{color}</span>
                            </label>
                          ))}
                        </div>

                        {/* Agregar color personalizado */}
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            value={customColor}
                            onChange={e => setCustomColor(e.target.value.toUpperCase())}
                            placeholder="Otro color..."
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (customColor && !selectedColors.includes(customColor)) {
                                setSelectedColors([...selectedColors, customColor]);
                                setCustomColor('');
                              }
                            }}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                          >
                            + Agregar
                          </button>
                        </div>

                        {selectedColors.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedColors.map(color => (
                              <span key={color} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                {color}
                                <button
                                  type="button"
                                  onClick={() => setSelectedColors(selectedColors.filter(c => c !== color))}
                                  className="hover:text-blue-900"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Sección Tallas */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tallas:</label>
                        <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                          {(newProductData.marca ? brandSizes.filter(bs => bs.brand === newProductData.marca).map(bs => bs.size) : []).map(size => (
                            <label key={size} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={selectedSizes.includes(size)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedSizes([...selectedSizes, size]);
                                  } else {
                                    setSelectedSizes(selectedSizes.filter(s => s !== size));
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <span>{size}</span>
                            </label>
                          ))}
                        </div>

                        {/* Agregar talla personalizada */}
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            value={customSize}
                            onChange={e => setCustomSize(e.target.value.toUpperCase())}
                            placeholder="Otra talla..."
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (customSize && !selectedSizes.includes(customSize)) {
                                setSelectedSizes([...selectedSizes, customSize]);
                                setCustomSize('');
                              }
                            }}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                          >
                            + Agregar
                          </button>
                        </div>

                        {selectedSizes.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedSizes.map(size => (
                              <span key={size} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                {size}
                                <button
                                  type="button"
                                  onClick={() => setSelectedSizes(selectedSizes.filter(s => s !== size))}
                                  className="hover:text-green-900"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Preview de variantes */}
                      {selectedColors.length > 0 && selectedSizes.length > 0 && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                          <p className="text-sm font-medium text-blue-900">
                            Se crearán {selectedColors.length * selectedSizes.length} variantes
                          </p>
                          <p className="text-xs text-blue-700 mt-1">
                            {selectedColors.length} color(es) × {selectedSizes.length} talla(s)
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio Venta RETAIL ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newProductData.precio}
                    onChange={e => setNewProductData({ ...newProductData, precio: parseFloat(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-700 mb-1">Costo Proveedor USD (Mayoreo)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newProductData.costo || ''}
                    onChange={e => setNewProductData({ ...newProductData, costo: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full border border-purple-200 bg-purple-50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Opcional. Ej. 25.00"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Sirve para calcular el precio mayoreo automáticamente al vender.</p>
                </div>

                <div className="flex justify-end gap-3 pt-4 col-span-1 md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setShowProductModal(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
                  >
                    {loading
                      ? 'Guardando...'
                      : createMultiple && selectedColors.length > 0 && selectedSizes.length > 0
                        ? `Agregar ${selectedColors.length * selectedSizes.length} variantes`
                        : 'Crear y Agregar'
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmButtonColor={confirmModal.color as any}
      />

      <CashCutModal isOpen={showCashCutModal} onClose={() => setShowCashCutModal(false)} />

      {/* Pre-order Flow Modal */}
      <PreOrderFlowModal
        isOpen={showPreOrderFlowModal}
        onClose={() => {
          setShowPreOrderFlowModal(false);
          setPreOrderFlow(null);
          setPendingPreOrderProduct('');
        }}
        onSelect={handlePreOrderFlowSelect}
        productName={pendingPreOrderProduct}
      />



      {
        showScanner && (
          <ScannerModal
            isOpen={showScanner}
            onClose={() => setShowScanner(false)}
            onScan={handleScan}
          />
        )
      }
      <ConfirmationModal
        isOpen={showTransferConfirmation}
        onClose={() => setShowTransferConfirmation(false)}
        onConfirm={() => {
          setShowTransferConfirmation(false);
          executeSaleProcess();
        }}
        title="Confirmar Transferencia"
        message={
          <div className="flex flex-col gap-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-start gap-2">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold">Verificar Pago:</span>
                <p>Por favor asegúrate de que la transferencia ya haya sido realizada y verificada en la cuenta bancaria antes de confirmar la venta.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">¿Deseas procesar la venta ahora?</p>
          </div>
        }
        confirmText="Confirmar y Procesar"
        cancelText="Cancelar"
        confirmButtonColor="blue"
      />
    </Layout >
  );
};


// Modal de Opciones de Ticket


/*
// const TicketOptionsModal_Deprecated: React.FC<any> = ({sale, onPrint, onWhatsApp, onClose}) => {
return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
              <div className="mb-4 flex flex-col items-center">
                <div className="p-4 bg-green-100 rounded-full mb-4">
                  <div className="text-green-600 font-bold text-3xl">✓</div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Venta Completada</h2>
                <p className="text-gray-600 font-medium">Ticket #{sale.id}</p>
                <p className="text-sm text-gray-500 mt-1">Total: ${sale.total.toFixed(2)}</p>
              </div>

              <p className="text-gray-600 mb-6">
                ¿Cómo deseas entregar el comprobante?
              </p>

              <div className="space-y-3">
                <button
                  onClick={onPrint}
                  className="w-full flex items-center justify-center gap-3 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                >
                  <Printer size={20} />
                  Imprimir Ticket
                </button>

                <button
                  onClick={onWhatsApp}
                  disabled={!sale.customerPhone}
                  className={`w-full flex items-center justify-center gap-3 py-3 rounded-lg font-bold transition ${sale.customerPhone
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  <MessageCircle size={20} />
                  Enviar por WhatsApp
                  {!sale.customerPhone && <span className="text-xs font-normal">(Sin teléfono)</span>}
                </button>

                <button
                  onClick={onClose}
                  className="w-full py-2 text-gray-500 hover:text-gray-700 font-medium mt-2"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
          );
          // };
          */

// Modal de confirmación de inventario
export interface InventoryModalProps {
  onConfirm: (deduct: boolean) => void;
  onClose: () => void;
  hasMixedItems?: boolean;
}

const InventoryModal: React.FC<InventoryModalProps> = ({ onConfirm, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-yellow-100 rounded-full">
            <AlertCircle size={24} className="text-yellow-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Descontar del inventario</h2>
        </div>
        <p className="text-gray-600 mb-6">
          ¿Deseas descontar estos productos del inventario? Esta acción reducirá el stock disponible.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => onConfirm(false)}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            No descontar
          </button>
          <button
            onClick={() => onConfirm(true)}
            className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Sí, descontar
          </button>
        </div>
        <button onClick={onClose} className="w-full mt-3 py-2 text-gray-500 hover:text-gray-700 transition">
          Cancelar
        </button>
      </div>
    </div>
  );
};

// Modal de crear ticket
interface TicketModalProps {
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  cartItems: CartItem[];
  onSubmit: (data: CreateTicketRequest) => void;
  onClose: () => void;
  loading: boolean;
}

const TicketModal: React.FC<TicketModalProps> = ({
  customerName,
  customerPhone,
  customerEmail,
  cartItems,
  onSubmit,
  onClose,
  loading,
}) => {
  // Auto-llenar códigos de productos de la nota de venta
  const productCodes = cartItems
    .map(item => item.product.cod_producto)
    .filter(code => code && code.trim() !== '')
    .join(', ');

  const [title, setTitle] = useState('Ajuste de prenda');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'>('MEDIUM');
  const [deliveryType, setDeliveryType] = useState<'IN_STORE' | 'UBER'>('IN_STORE');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [modelCode] = useState(productCodes);
  const [adjustments, setAdjustments] = useState('');
  const [localCustomerPhone, setLocalCustomerPhone] = useState(customerPhone || '');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim()) {
      toast.error('Por favor describe los ajustes necesarios');
      return;
    }

    if (!localCustomerPhone.trim()) {
      toast.error('El teléfono del cliente es requerido para notificaciones');
      return;
    }

    if (deliveryType === 'UBER' && !deliveryAddress.trim()) {
      toast.error('Por favor ingresa la dirección de entrega');
      return;
    }

    onSubmit({
      title,
      description,
      customerName,
      customerPhone: localCustomerPhone,
      priority,
      dueDate: dueDate || undefined,
      deliveryType,
      deliveryAddress: deliveryType === 'UBER' ? deliveryAddress : undefined,
      modelCode: modelCode || undefined,
      adjustments: adjustments || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Scissors size={24} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Crear Ticket de Ajustes</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <User size={18} />
              Información del cliente
            </h3>
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={customerName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono de contacto *
                </label>
                <input
                  type="tel"
                  value={localCustomerPhone}
                  onChange={(e) => setLocalCustomerPhone(e.target.value)}
                  placeholder="Ej: 5512345678"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  required
                />
              </div>
              {customerEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={customerEmail}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-900"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título del ticket</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción general *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Describe el servicio o trabajo a realizar..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ajustes específicos
            </label>
            <textarea
              value={adjustments}
              onChange={(e) => setAdjustments(e.target.value)}
              rows={3}
              placeholder="Ej: Subir bastilla 3cm, ajustar cintura, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código/Modelo (Auto-generado)
              </label>
              <input
                type="text"
                value={modelCode}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
              />
              {cartItems.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {cartItems.length} producto{cartItems.length > 1 ? 's' : ''} en el ticket
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              >
                <option value="LOW">Baja</option>
                <option value="MEDIUM">Media</option>
                <option value="HIGH">Alta</option>
                <option value="URGENT">Urgente</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de entrega</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de entrega</label>
              <select
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              >
                <option value="IN_STORE">En tienda</option>
                <option value="UBER">Uber/Domicilio</option>
              </select>
            </div>
          </div>

          {/* Campo de dirección solo si es entrega por Uber */}
          {deliveryType === 'UBER' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección de entrega *
              </label>
              <textarea
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                rows={2}
                placeholder="Calle, número, colonia, referencias..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Esta dirección será visible para la modista en el ticket
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas adicionales
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Información adicional, recordatorios, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creando...' : 'Crear Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal de Autenticación de Vendedora
interface SalesAuthModalProps {
  onConfirm: (code: string, pass: string) => void;
  onClose: () => void;
  loading: boolean;
}

const SalesAuthModal: React.FC<SalesAuthModalProps> = ({ onConfirm, onClose, loading }) => {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || !password) {
      toast.error('Por favor ingresa tu código y contraseña');
      return;
    }
    onConfirm(code, password);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4 justify-center">
          <div className="p-3 bg-blue-100 rounded-full">
            <Lock size={24} className="text-blue-600" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Autenticación Requerida</h2>
        <p className="text-gray-500 text-center mb-6 text-sm">
          Ingresa tu código de vendedora y contraseña para registrar esta venta.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código de Vendedora</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Código de 5 dígitos"
              maxLength={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Verificando...' : 'Confirmar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
